import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Star, Users, BedDouble, Bath, MapPin, Sparkles, Camera } from "lucide-react";
import { api } from "@/lib/api";
import StickyBookingCard from "@/components/property/StickyBookingCard";
import PhotoExplorer from "@/components/property/PhotoExplorer";
import HeroCarousel from "@/components/property/HeroCarousel";
import BrandedLoader from "@/components/common/BrandedLoader";
import PropertyReviewsModal from "@/components/property/PropertyReviewsModal";
import ReviewCard from "@/components/common/ReviewCard";
import ShareStayButton from "@/components/common/ShareStayButton";
import SocialValidation from "@/components/common/SocialValidation";
import HouseRulesDialog from "@/components/common/HouseRulesDialog";
import { buildMediaList } from "@/lib/media";
import { amenityIcon, labelOf } from "@/lib/amenityIcons";
import PropertyCard from "@/components/property/PropertyCard";
import { track } from "@/components/common/Analytics";
import SafeHtml from "@/components/common/SafeHtml";
import StructuredData, { lodgingSchema, breadcrumbSchema, reviewSchema } from "@/components/common/StructuredData";
import useSiteConfig from "@/lib/useSiteConfig";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function PropertyDetail() {
  const cfg = useSiteConfig();
  const { slug } = useParams();
  const [property, setProperty] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [related, setRelated] = useState([]);
  const [lightbox, setLightbox] = useState(false);
  const [lightboxStart, setLightboxStart] = useState(null);
  const [reviewsModal, setReviewsModal] = useState({ open: false, star: "all" });
  // Live quote bubbled up from StickyBookingCard so the mobile reserve bar
  // mirrors the same price the guest sees in the booking widget — instead
  // of showing a stale "from ₹X / night" fallback that mismatches the
  // real, live API total.
  const [liveQuote, setLiveQuote] = useState(null);
  const [liveDates, setLiveDates] = useState(null);
  const openReviewsModal = (star = "all") => setReviewsModal({ open: true, star });

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/properties/${slug}`).then(({ data }) => {
      setProperty(data);
      // Funnel event — viewing a listing
      track("view_content", {
        content_type: "stay",
        content_ids: [data.slug],
        content_name: data.display_name,
        value: data.nightly_price_from,
        currency: "INR",
      });
      api.get("/properties", { params: { collection: data.collections?.[0] } })
        .then(({ data: rel }) => setRelated(rel.filter((r) => r.slug !== slug).slice(0, 3)));
    });
    api.get("/reviews", { params: { property_slug: slug } }).then(({ data }) => setReviews(data));
    // Load per-property FAQs; fall back to global if empty.
    api.get("/faqs", { params: { scope: "property", property_slug: slug } }).then(async ({ data }) => {
      if (data && data.length) { setFaqs(data); return; }
      const g = await api.get("/faqs", { params: { scope: "global" } });
      setFaqs(g.data || []);
    });
  }, [slug]);

  if (!property) {
    return <BrandedLoader />;
  }

  const images = [property.hero_image, ...property.gallery];
  const media = buildMediaList(property);
  const videoCount = media.filter((m) => m.type === "video").length;
  const openExplorer = (startIdx = null) => {
    // When the user clicked a specific media tile, jump straight into the
    // zoomed view so videos start playing without a second tap.
    const start = typeof startIdx === "number" ? startIdx : null;
    setLightboxStart(start);
    setLightbox(true);
  };

  return (
    <div data-testid="property-detail" className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10 lg:py-12">
      <Helmet>
        <title>{`${property.public_name} · Vyagram Experiences`}</title>
        <meta name="description" content={property.short_description} />
        {/* Open Graph / Twitter / WhatsApp share preview — uses the property's
            hero photo so a link drop renders the actual stay rather than the
            site default. */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${property.public_name} · Vyagram Experiences`} />
        <meta property="og:description" content={property.short_description} />
        <meta property="og:image" content={property.hero_image} />
        <meta property="og:image:secure_url" content={property.hero_image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="900" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${property.public_name} · Vyagram`} />
        <meta name="twitter:description" content={property.short_description} />
        <meta name="twitter:image" content={property.hero_image} />
        <link rel="canonical" href={`https://vyagram.com/stays/${property.slug}`} />
      </Helmet>

      {/* JSON-LD: full LodgingBusiness with image gallery, aggregate rating
          and breadcrumbs. Top reviews are surfaced as Review nodes so they
          can qualify for review snippets in SERPs. */}
      <StructuredData
        id="property-schema"
        data={[
          lodgingSchema(property),
          breadcrumbSchema([
            { name: "Home", url: "https://vyagram.com/" },
            { name: "Stays", url: "https://vyagram.com/stays" },
            { name: property.display_name || property.public_name, url: `https://vyagram.com/stays/${property.slug}` },
          ]),
          ...reviews.slice(0, 5).map((r) => reviewSchema(r, property.display_name)),
        ]}
      />

      {/* Title */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {property.guest_favourite && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-amber">
              <Sparkles size={12} /> Guest Favourite
            </span>
          )}
          {property.seo_tags?.slice(0, 6).map((t) => (
            <span key={t} className="vyg-pill !py-1 !px-3 !text-xs">{t}</span>
          ))}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl">{property.display_name}</h1>
        <p className="text-lg text-forest/70">{property.tagline}</p>
        <div className="flex flex-wrap items-center gap-4 text-sm text-noir/70">
          <span className="inline-flex items-center gap-1"><Star size={14} className="fill-gold text-gold" /><span className="font-medium text-forest">{Number(property.stats_avg_rating || property.rating || 0).toFixed(2)}</span> · {property.stats_total_reviews || property.review_count} reviews</span>
          <span className="inline-flex items-center gap-1"><MapPin size={14}/> {property.location_label}</span>
          <ShareStayButton property={property} />
        </div>
      </div>

      {/* Mobile: booking card comes FIRST — above gallery for conversion focus */}
      <div className="mt-6 lg:hidden">
        <StickyBookingCard property={property} onQuoteChange={(q, d) => { setLiveQuote(q); setLiveDates(d); }} />
      </div>

      {/* Per-property Social Validation — immediately below availability card on mobile */}
      <SocialValidation variant="compact" property={property} onOpenReviews={openReviewsModal} className="mt-5 lg:hidden" />

      {/* Gallery */}
      <div className="mt-6">
        <HeroCarousel items={media} propertyName={property.display_name} onExpand={openExplorer} />
        <button onClick={openExplorer} data-testid="show-all-photos" className="mt-3 vyg-pill">
          <Camera size={14} /> Show all {images.length} photos{videoCount > 0 ? ` + ${videoCount} video${videoCount > 1 ? "s" : ""}` : ""}
        </button>
      </div>

      <PhotoExplorer open={lightbox} onClose={() => setLightbox(false)} items={media} propertyName={property.display_name} startIndex={lightboxStart} />
      <PropertyReviewsModal
        open={reviewsModal.open}
        onClose={() => setReviewsModal({ open: false, star: "all" })}
        property={property}
        initialStar={reviewsModal.star}
      />

      <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_380px]">
        {/* Main */}
        <div className="min-w-0">
          {/* Facts row — clean, without the house-rules button squeezed in */}
          <div className="flex flex-wrap items-center gap-5 rounded-xl bg-white p-5 text-sm shadow-card">
            <Fact icon={Users} label={`${property.guests} guests`} />
            <Fact icon={BedDouble} label={`${property.bedrooms} bedrooms`} />
            <Fact icon={Bath} label={`${property.bathrooms} baths`} />
            <Fact icon={MapPin} label={property.floor} />
          </div>

          {/* Dedicated action row — house rules + (future policy buttons) get breathing room */}
          <div className="mt-3 flex flex-wrap gap-2" data-testid="policy-action-bar">
            <HouseRulesDialog propertyName={property.display_name} slug={`${property.slug}-facts`} html={property.house_rules_html} variant="pill" />
          </div>

          <section className="mt-10">
            <h2 className="font-display text-3xl">About this stay</h2>
            <p className="editorial-body mt-4 whitespace-pre-line">{property.listing_description}</p>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-3xl">The Space</h2>
            <p className="editorial-body mt-4 whitespace-pre-line">{property.space}</p>
          </section>

          <section className="mt-12 rounded-2xl bg-mist/60 p-8">
            <h2 className="font-display text-2xl">Good to know</h2>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {property.highlights?.map((h, i) => {
                const Icon = amenityIcon(h);
                const text = labelOf(h);
                return (
                  <li key={i} className="flex items-start gap-3 text-sm text-noir/80">
                    <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-forest shadow-card">
                      <Icon size={15} />
                    </span>
                    <span className="editorial-body !text-base">{text}</span>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-3xl">What&rsquo;s included</h2>
            <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {property.amenities?.map((a, i) => {
                const Icon = amenityIcon(a);
                const text = labelOf(a);
                return (
                  <div
                    key={`${text}-${i}`}
                    className="flex items-center gap-3 rounded-lg border border-forest/10 bg-white px-4 py-3 text-sm"
                    data-testid={`amenity-${text.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <Icon size={16} className="shrink-0 text-forest" />
                    <span>{text}</span>
                  </div>
                );
              })}
            </div>
          </section>

          {reviews.length > 0 && (
            <section className="mt-12" id="reviews">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <h2 className="font-display text-3xl flex items-center gap-3">
                  <Star size={22} className="fill-gold text-gold" /> {Number(property.stats_avg_rating || property.rating || 0).toFixed(2)} <span className="text-noir/60 text-lg font-sans">· {property.stats_total_reviews || property.review_count} reviews</span>
                </h2>
                <button
                  type="button"
                  onClick={() => openReviewsModal("all")}
                  data-testid="see-all-reviews-top"
                  className="inline-flex items-center gap-2 rounded-full border border-forest/25 bg-white px-4 py-2 text-xs font-medium text-forest transition hover:bg-mist/60"
                >
                  See all reviews →
                </button>
              </div>
              <div className="mt-6 grid gap-5 md:grid-cols-2">
                {reviews.slice(0, 4).map((r) => (<ReviewCard key={r.id} r={r} propertySlug={property.slug} propertyName={property.display_name} />))}
              </div>
              <button
                type="button"
                onClick={() => openReviewsModal("all")}
                data-testid="see-all-reviews"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-forest/25 bg-white px-5 py-2.5 text-sm font-medium text-forest transition hover:bg-mist/60"
              >
                See all {property.stats_total_reviews || property.review_count || reviews.length} reviews →
              </button>
            </section>
          )}

          <section className="mt-12 rounded-2xl bg-forest p-8 text-parchment">
            <h2 className="font-display text-3xl text-parchment">Hosted by Vyagram</h2>
            <p className="editorial-body mt-4 !text-parchment/85">{property.interaction}</p>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-3xl">{property.location_label}</h2>
            {property.location_about_html ? (
              <SafeHtml html={property.location_about_html} className="editorial-body mt-4 prose-editorial" testId="location-about-body" />
            ) : (
              <p className="editorial-body mt-4">
                Tucked into a thoughtfully chosen pocket of {property.city}, this stay sits close to the city&apos;s best
                cafés, design stores and slow corners — while staying private, quiet and easy to come home to.
              </p>
            )}
          </section>

          <section className="mt-12">
            <h2 className="font-display text-3xl">Questions, answered.</h2>
            <Accordion type="single" collapsible className="mt-5">
              {faqs.map((f, j) => (
                <AccordionItem key={f.id || j} value={f.id || `q${j}`}>
                  <AccordionTrigger className="font-editorial text-lg">{f.question}</AccordionTrigger>
                  <AccordionContent className="editorial-body">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        </div>

        {/* Right rail — desktop only; mobile renders booking card above */}
        <div className="hidden lg:block">
          <StickyBookingCard property={property} onQuoteChange={(q, d) => { setLiveQuote(q); setLiveDates(d); }} />
          {/* Per-property social validation directly below the booking card on desktop */}
          <SocialValidation variant="compact" property={property} onOpenReviews={openReviewsModal} className="mt-5" />
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-20">
          <h2 className="font-display text-3xl">Similar stays</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {related.map((p) => (<PropertyCard key={p.slug} p={p} />))}
          </div>
        </section>
      )}

      {/* Mobile reserve bar — mirrors the live quote total whenever one is
          available so the price here never contradicts the booking widget
          above. Falls through to "Live pricing" loader / "from ₹X" only as
          the last resort while the API hasn't returned yet. */}
      <div className="reserve-bar flex items-center justify-between gap-3" data-testid="mobile-reserve-bar">
        <div>
          {liveQuote && liveQuote.available !== false ? (
            (() => {
              // Compute estimated savings vs Airbnb — uses the global
              // airbnb_markup_pct from site config (default 15%) to
              // approximate what the same dates + guests would cost on an
              // OTA listing. Only shown when the savings is meaningful
              // (>= ₹500) so a near-identical price doesn't render a
              // misleading "Save ₹0" badge.
              const pct = Number(cfg.airbnb_markup_pct || 15) / 100;
              const otaTotal = Math.round(liveQuote.total * (1 + pct));
              const savings = otaTotal - liveQuote.total;
              return (
                <>
                  <p className="text-[10px] uppercase tracking-wider text-amber">Total · {liveQuote.nights} {liveQuote.nights === 1 ? "night" : "nights"}</p>
                  <p className="font-display text-lg text-forest" data-testid="mobile-reserve-price">₹{liveQuote.total.toLocaleString("en-IN")}</p>
                  {savings >= 500 && (
                    <p className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700" data-testid="mobile-reserve-savings">
                      Direct rate · Save ₹{savings.toLocaleString("en-IN")} vs Airbnb
                    </p>
                  )}
                </>
              );
            })()
          ) : liveQuote && liveQuote.available === false ? (
            <>
              <p className="text-[10px] uppercase tracking-wider text-amber">Dates blocked</p>
              <p className="font-display text-base text-forest">Pick new dates</p>
            </>
          ) : (
            <>
              <p className="text-[10px] uppercase tracking-wider text-amber">From</p>
              <p className="font-display text-lg text-forest" data-testid="mobile-reserve-price">₹{property.nightly_price_from.toLocaleString("en-IN")} <span className="text-xs text-noir/60 font-sans">/ night</span></p>
            </>
          )}
        </div>
        <Link
          to={liveDates ? `/booking?slug=${property.slug}&check_in=${liveDates.checkIn}&check_out=${liveDates.checkOut}&guests=${liveDates.guests}` : `/booking?slug=${property.slug}`}
          className="vyg-cta"
          data-testid="mobile-reserve-cta"
        >Reserve</Link>
      </div>
    </div>
  );
}

function Fact({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-noir/80"><Icon size={14} className="text-forest" /> {label}</span>
  );
}
